import { CELL_VISUALIZATIONS_SESSION_VERSION_PREFILTER } from 'constants/routes'
import { Stack } from '@mui/material'
import { DeepcellPrimaryButton } from 'components/shared'
import { isEqual } from 'lodash'
import { useCallback, useMemo, useRef, useState } from 'react'
import { generatePath, useHistory } from 'react-router-dom'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import { useNotificationSlice } from 'redux/slices/hooks/useNotificationSlice'
import { DifferentialFeaturesComparison } from 'redux/slices/types'
import useRecomputeDifferentialFeatures from '../useRecomputeDifferentialFeatures'
import useCellVisualizationUrlParams from '../useCellVisualizationUrlParams'
import useSessionApi from '../useSessionApi'
import { useCurrentCellCountAfterPrefilters } from '../prefilter/useCurrentCellCountAfterPrefilters'
import TopUpUMAPDialog from '../prefilter/TopUpUMAPDialog'

export const PreFilterButtons = (): JSX.Element => {
  const history = useHistory()
  const { exitPreFilter, sessionId, versionId } = useCellVisualizationUrlParams()
  const { saveVersionConfig } = useSessionApi()
  const { recomputeAllComparisons } = useRecomputeDifferentialFeatures()
  const { displayNotification } = useNotificationSlice()
  const {
    dataFrame,
    cellVisualizations: { morphotypesToRemove, differentialFeaturesComparisons, max_cell_count: maxCellCount },
    prefilteredCellIdSet,
    visiblePinnedCells: pinnedCells,
    setMorphotypesToRemove,
    addDifferentialFeaturesComparison,
    deleteDifferentialFeaturesComparisonByTaskId,
  } = useCellVisualizationsSlice()

  const [showTopUpDialog, setShowTopUpDialog] = useState(false)

  const mountedCellIds = useRef(prefilteredCellIdSet)
  const mountedMorphotypesToRemove = useRef(morphotypesToRemove)

  const currentCellCount = useCurrentCellCountAfterPrefilters()
  const shouldOfferTopUp = Boolean(maxCellCount && currentCellCount < maxCellCount)

  const valsAreUnchanged = useMemo(
    () => isEqual(mountedCellIds.current, prefilteredCellIdSet),
    [prefilteredCellIdSet]
  )

  const continueAfterSave = useCallback(async (newVersionId: number) => {
    const comparisonCount = differentialFeaturesComparisons?.length || 0
    const hasComparisons = comparisonCount > 0

    if (hasComparisons) {
      displayNotification({
        message: `Recomputing ${comparisonCount} differential feature comparison${comparisonCount > 1 ? 's' : ''}…`,
        type: 'info',
        showProgress: true,
        closeOnClickaway: false,
        timeout: null,
      })
    }

    if (!dataFrame) {
      displayNotification({
        message: 'Error: Cell data not loaded. Please reload the session.',
        type: 'error',
      })
      return
    }

    const recomputedComparisons = await recomputeAllComparisons(
      differentialFeaturesComparisons,
      sessionId,
      newVersionId,
      dataFrame,
      pinnedCells || []
    )

    differentialFeaturesComparisons?.forEach((comparison) => {
      deleteDifferentialFeaturesComparisonByTaskId({ taskId: comparison.taskId })
    })
    recomputedComparisons.reverse().forEach((comparison: DifferentialFeaturesComparison) => {
      addDifferentialFeaturesComparison(comparison)
    })

    displayNotification({
      message: hasComparisons ? 'Prefilter saved and differential features updated!' : 'Prefilter saved successfully!',
      type: 'success',
      timeout: 3000,
      closeOnClickaway: true,
    })
  }, [
    differentialFeaturesComparisons,
    displayNotification,
    dataFrame,
    recomputeAllComparisons,
    sessionId,
    pinnedCells,
    deleteDifferentialFeaturesComparisonByTaskId,
    addDifferentialFeaturesComparison,
  ])

  const performSaveAndContinue = useCallback(async () => {
    displayNotification({
      message: 'Saving prefilter changes…',
      type: 'info',
      showProgress: true,
      closeOnClickaway: false,
      timeout: null,
    })

    const error = await saveVersionConfig()
    if (error) return
    const newVersionId = versionId + 1
    await continueAfterSave(newVersionId)
  }, [displayNotification, saveVersionConfig, versionId, continueAfterSave])

  const saveAndGetVersionId = useCallback(async (): Promise<number> => {
    await saveVersionConfig({ skipNavigation: true })
    const newVersionId = versionId + 1
    history.replace(generatePath(CELL_VISUALIZATIONS_SESSION_VERSION_PREFILTER, { sessionId: String(sessionId), versionId: String(newVersionId) }))
    return newVersionId
  }, [saveVersionConfig, versionId, history, sessionId])

  const onSave = useCallback(async () => {
    if (shouldOfferTopUp) {
      setShowTopUpDialog(true)
      return
    }
    await performSaveAndContinue()
  }, [shouldOfferTopUp, performSaveAndContinue])

  const onSkip = () => {
    if (!isEqual(mountedMorphotypesToRemove.current, morphotypesToRemove)) {
      setMorphotypesToRemove(mountedMorphotypesToRemove.current)
    }
    exitPreFilter()
  }

  const handleDialogDismiss = useCallback(() => {
    setShowTopUpDialog(false)
  }, [])

  const handleDialogCancel = useCallback(async () => {
    setShowTopUpDialog(false)
    await performSaveAndContinue()
  }, [performSaveAndContinue])

  const handleDialogSuccess = useCallback(() => {
    setShowTopUpDialog(false)
  }, [])

  return (
    <>
      <Stack direction="row" spacing={2}>
        <DeepcellPrimaryButton
          sx={{ height: '37px', width: '240px', border: '1px solid rgba(164, 239, 192, 1)' }}
          contained
          onClick={onSkip}
        >
          Skip to cell visualization
        </DeepcellPrimaryButton>
        <DeepcellPrimaryButton
          sx={{ height: '37px', width: '172px', border: '1px solid rgba(164, 239, 192, 1)' }}
          onClick={onSave}
          contained
          disabled={valsAreUnchanged}
        >
          Save & Continue
        </DeepcellPrimaryButton>
      </Stack>

      <TopUpUMAPDialog
        open={showTopUpDialog}
        onClose={handleDialogDismiss}
        onCancel={handleDialogCancel}
        onSuccess={handleDialogSuccess}
        sessionId={sessionId}
        onSaveFirst={saveAndGetVersionId}
      />
    </>
  )
}

export default PreFilterButtons
